/**
 * roadmap-agent-history.ts — trim the client-held agent thread on complete
 * user-turns so a later send never starts mid tool-pair.
 */

/** Minimum shape needed to keep user-turns and tool pairs intact. */
export interface ReplayableHistoryMessage {
  role: 'user' | 'assistant' | 'tool';
  toolCalls?: Array<{ id: string }>;
  toolCallId?: string;
}

/**
 * Keep the newest complete user-turns that fit in `maxMessages`.
 * A user-turn is a user message plus every assistant/tool message until the
 * next user. Never starts on a dangling tool result or a torn tool pair.
 */
export function takeReplayableHistory<T extends ReplayableHistoryMessage>(
  messages: T[],
  maxMessages: number,
): T[] {
  if (maxMessages <= 0 || messages.length === 0) return [];

  const userStarts: number[] = [];
  for (let i = 0; i < messages.length; i++) {
    if (messages[i]?.role === 'user') userStarts.push(i);
  }

  let from = userStarts.length > 0 ? userStarts[userStarts.length - 1]! : 0;
  for (let i = userStarts.length - 2; i >= 0; i--) {
    const candidate = userStarts[i]!;
    if (messages.length - candidate <= maxMessages) {
      from = candidate;
    } else {
      break;
    }
  }

  let slice = messages.slice(from);
  if (slice.length > maxMessages) {
    slice = slice.slice(-maxMessages);
  }
  return dropLeadingIncomplete(slice);
}

function dropLeadingIncomplete<T extends ReplayableHistoryMessage>(messages: T[]): T[] {
  let slice = messages;
  while (slice.length > 0 && slice[0]?.role === 'tool') {
    slice = slice.slice(1);
  }

  const first = slice[0];
  if (!first || first.role !== 'assistant') return slice;

  const calls = first.toolCalls ?? [];
  if (calls.length === 0) return slice;

  const found = new Set<string>();
  for (let i = 1; i < slice.length; i++) {
    const next = slice[i];
    if (next?.role !== 'tool') break;
    if (next.toolCallId) found.add(next.toolCallId);
  }
  const complete = calls.every((call) => found.has(call.id));
  if (complete) return slice;

  let dropThrough = 1;
  while (dropThrough < slice.length && slice[dropThrough]?.role === 'tool') {
    dropThrough += 1;
  }
  return dropLeadingIncomplete(slice.slice(dropThrough));
}
